/**
 * ConversationCard — feed-like card for chatbot responses (one editorial recipe).
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  BookmarkIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  UserIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { AuraFormattedText as FormattedText } from './AuraFormattedText';
import StrategyCardContent from './StrategyCardContent';
import ProgressCardContent from './ProgressCardContent';
import CardSaveButton, { isAdviceCard } from './CardSaveButton';
import SaveDiscoverabilityHint from './SaveDiscoverabilityHint';
import TalkToAdvisorButton from '../advice/TalkToAdvisorButton';
import AdviceSaveActions from '../advice/AdviceSaveActions';

const CARD_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const EMPHASIS_TYPES = new Set(['strategy', 'proposal', 'progress']);

const normalizeCardType = (type) => {
  if (type === 'aura_reflection') return 'text';
  if (type === 'routing_response' || type === 'routing_suggestion') return 'routing';
  return type;
};

const getTypeLabel = (type) => {
  const labels = {
    user_intent: 'You',
    user_message: 'You',
    advisor_insight: 'Advice',
    advisor_response: 'Advice',
    gov_verdict: 'Guidance',
    gov_guidance: 'Guidance',
    activity_summary: 'Summary',
    next_steps: 'Next steps',
    learning_recommendations: 'Learning',
    routing: 'Suggestion',
    routing_response: 'Suggestion',
    routing_suggestion: 'Suggestion',
    text: 'Reflection',
    aura_reflection: 'Reflection',
    personalized_analysis: 'Analysis',
    vision_creation_prompt: 'Vision',
  };
  return labels[type] || 'Aura';
};

const getCardTitle = (card) => {
  if (card.title) return card.title;
  const t = card.type;
  if (t === 'user_intent' || t === 'user_message') return 'Your question';
  if (t === 'advisor_insight' || t === 'advisor_response') return "Aura's advice";
  if (t === 'personalized_analysis') return 'Personalized analysis';
  if (t === 'gov_verdict' || t === 'gov_guidance') return 'Government guidance';
  if (t === 'activity_summary') return 'Your situation';
  if (t === 'next_steps') return 'Fast next steps';
  if (t === 'learning_recommendations') return 'Recommended learning';
  if (t === 'routing_response') return "Aura's suggestion";
  if (t === 'routing_suggestion') return 'Check this out';
  if (t === 'vision_creation_prompt') return 'Create a vision';
  return "Aura's reflection";
};

const ConversationCard = ({
  card,
  index,
  user,
  onSaveNote,
  section = 'cashflow',
  visionId = null,
  showSaveHint = false,
}) => {
  const [showCitations, setShowCitations] = useState(false);
  const cardType = normalizeCardType(card.type);
  const canSave = isAdviceCard(card);
  const emphasis = EMPHASIS_TYPES.has(cardType);

  if (cardType === 'strategy') {
    return (
      <motion.div {...CARD_MOTION} transition={{ ...CARD_MOTION.transition, delay: index * 0.05 }} className="mb-4">
        {showSaveHint && <SaveDiscoverabilityHint user={user} visible />}
        <div className="flex justify-end gap-2 mb-2">
          <CardSaveButton card={card} sourceSurface={section} visionId={visionId} showLabel onSaved={onSaveNote} />
        </div>
        <StrategyCardContent card={card} visionId={visionId} showStepActions />
      </motion.div>
    );
  }

  if (cardType === 'progress') {
    return (
      <motion.div {...CARD_MOTION} transition={{ ...CARD_MOTION.transition, delay: index * 0.05 }} className="mb-4">
        {showSaveHint && <SaveDiscoverabilityHint user={user} visible />}
        <div className="flex justify-end gap-2 mb-2">
          <CardSaveButton card={card} sourceSurface={section} visionId={visionId} showLabel onSaved={onSaveNote} />
        </div>
        <ProgressCardContent card={card} />
      </motion.div>
    );
  }

  const getCardIcon = () => {
    switch (card.type) {
      case 'user_intent':
      case 'user_message':
        return <UserIcon className="w-5 h-5" />;
      case 'advisor_insight':
      case 'advisor_response':
      case 'personalized_analysis':
        return <LightBulbIcon className="w-5 h-5" />;
      case 'gov_verdict':
      case 'gov_guidance':
        return <ShieldCheckIcon className="w-5 h-5" />;
      case 'aura_reflection':
      case 'activity_summary':
        return <SparklesIcon className="w-5 h-5" />;
      case 'next_steps':
        return <CheckCircleIcon className="w-5 h-5" />;
      case 'learning_recommendations':
      case 'routing_suggestion':
        return <BookmarkIcon className="w-5 h-5" />;
      case 'routing_response':
        return <QuestionMarkCircleIcon className="w-5 h-5" />;
      case 'vision_creation_prompt':
        return <CheckCircleIcon className="w-5 h-5" />;
      default:
        return null;
    }
  };

  const getGovCheckBadge = () => {
    if (!card.gov_check_status) return null;
    const statusStyles = {
      pass: 'border-pine bg-pine-tint text-pine',
      flagged: 'border-warning bg-bone text-warning',
      unclear: 'border-hairline bg-bone-raised text-ink-muted',
    };
    const statusIcons = {
      pass: <CheckCircleIcon className="w-4 h-4" />,
      flagged: <ExclamationTriangleIcon className="w-4 h-4" />,
      unclear: <QuestionMarkCircleIcon className="w-4 h-4" />,
    };
    const statusLabels = {
      pass: 'Gov check: pass',
      flagged: 'Gov check: flagged',
      unclear: 'Gov check: unclear',
    };
    return (
      <button
        type="button"
        onClick={() => setShowCitations(!showCitations)}
        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-label transition-colors duration-300 ${statusStyles[card.gov_check_status]}`}
      >
        {statusIcons[card.gov_check_status]}
        <span>{statusLabels[card.gov_check_status]}</span>
      </button>
    );
  };

  const subBlock = 'py-3 border-t border-hairline first:border-t-0';
  const listItem = 'flex items-start gap-2 text-body text-ink';

  return (
    <>
      {showSaveHint && canSave && <SaveDiscoverabilityHint user={user} visible />}
      <motion.div
        {...CARD_MOTION}
        transition={{ ...CARD_MOTION.transition, delay: (index || 0) * 0.05 }}
        className={`aura-card light-surface mb-4 ${emphasis ? 'border-t-2 border-t-pine' : ''}`}
      >
        <div className="flex items-start justify-between mb-4 pb-4 border-b border-hairline">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-pine flex items-center justify-center text-primary shrink-0">
              {getCardIcon()}
            </div>
            <div>
              <h3 className="font-sans text-h3 text-ink">{getCardTitle(card)}</h3>
              <p className="text-label text-ink-muted">{getTypeLabel(card.type)}</p>
              {card.timestamp && (
                <p className="text-label text-ink-faint mt-0.5">
                  {new Date(card.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {getGovCheckBadge()}
            {canSave && (
              <CardSaveButton card={card} sourceSurface={section} visionId={visionId} showLabel onSaved={onSaveNote} />
            )}
          </div>
        </div>

        <div className="space-y-0">
          {card.quick_answer && (
            <div className={subBlock}>
              <p className="text-label text-ink-muted mb-2">Quick answer</p>
              <p className="text-body text-ink leading-relaxed">{card.quick_answer}</p>
            </div>
          )}

          {card.type === 'activity_summary' && (
            <div className={subBlock}>
              {card.key_observations?.length > 0 && (
                <div className="mb-4">
                  <p className="text-label text-ink-muted mb-2">Key observations</p>
                  <ul className="space-y-2">
                    {card.key_observations.map((obs, idx) => (
                      <li key={idx} className={listItem}>
                        <span className="text-ink-faint mt-1 shrink-0">·</span>
                        <span>{obs}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {card.risks?.length > 0 && (
                <div className="mb-4">
                  <p className="text-label text-warning mb-2">Risks and constraints</p>
                  <ul className="space-y-2">
                    {card.risks.map((risk, idx) => (
                      <li key={idx} className={listItem}>
                        <span className="text-warning mt-1 shrink-0">·</span>
                        <span>{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {card.opportunities?.length > 0 && (
                <div>
                  <p className="text-label text-pine mb-2">Opportunities</p>
                  <ul className="space-y-2">
                    {card.opportunities.map((opp, idx) => (
                      <li key={idx} className={listItem}>
                        <span className="text-pine mt-1 shrink-0">·</span>
                        <span>{opp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {card.confidence_score !== undefined && (
                <p className="text-label text-ink-muted mt-4">
                  Confidence: {Math.round(card.confidence_score * 100)}%
                </p>
              )}
            </div>
          )}

          {card.type === 'personalized_analysis' && (
            <div className={subBlock}>
              <div className="text-body text-ink leading-relaxed mb-4">
                <FormattedText content={card.content} />
              </div>
              {card.implications?.length > 0 && (
                <div className="mb-4">
                  <p className="text-label text-ink-muted mb-2">What that implies</p>
                  <ul className="space-y-2">
                    {card.implications.map((imp, idx) => (
                      <li key={idx} className={listItem}>
                        <span className="text-ink-faint shrink-0">·</span>
                        <span>{imp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {card.leverage_points?.length > 0 && (
                <div>
                  <p className="text-label text-ink-muted mb-2">Where your biggest leverage is</p>
                  <ul className="space-y-2">
                    {card.leverage_points.map((point, idx) => (
                      <li key={idx} className={listItem}>
                        <span className="text-ink-faint shrink-0">·</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {card.type === 'next_steps' && card.actions?.length > 0 && (
            <div className={`${subBlock} hairline-list`}>
              {card.actions.map((action, idx) => (
                <div
                  key={idx}
                  className="py-3 cursor-pointer hover:bg-pine-tint transition-colors duration-300"
                  onClick={() => {
                    if (action.deep_link) window.location.href = action.deep_link;
                  }}
                >
                  <p className="text-body font-medium text-ink mb-1">{action.title}</p>
                  <p className="text-label text-ink-muted">{action.description}</p>
                </div>
              ))}
            </div>
          )}

          {card.type === 'learning_recommendations' && card.recommendations?.length > 0 && (
            <div className={`${subBlock} hairline-list`}>
              {card.recommendations.map((reco, idx) => (
                <div
                  key={idx}
                  className="py-3 cursor-pointer hover:bg-pine-tint transition-colors duration-300"
                  onClick={() => {
                    if (reco.deep_link) window.location.href = reco.deep_link;
                  }}
                >
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-body font-medium text-ink">{reco.title}</p>
                    <span className="text-label px-2 py-0.5 rounded-full border border-hairline text-ink-muted">
                      {reco.difficulty}
                    </span>
                    <span className="text-label text-ink-faint">{reco.estimated_time}</span>
                  </div>
                  <p className="text-label text-ink-muted mb-1">{reco.description}</p>
                  {reco.why_matches && (
                    <p className="text-label text-ink-muted italic">{reco.why_matches}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {card.type === 'gov_guidance' && (
            <div className={subBlock}>
              <div className="text-body text-ink leading-relaxed mb-4">
                <FormattedText content={card.content} />
              </div>
              {card.sources?.length > 0 && (
                <div className="hairline-list">
                  <p className="text-label text-ink-muted py-2">Government sources</p>
                  {card.sources.map((source, idx) => (
                    <a
                      key={idx}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-3 text-body text-pine hover:underline"
                    >
                      {source.title}
                      {source.source && (
                        <span className="text-label text-ink-faint ml-2">({source.source})</span>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {!['activity_summary', 'personalized_analysis', 'next_steps', 'learning_recommendations', 'gov_guidance', 'advisor_response', 'routing_response', 'routing_suggestion', 'routing', 'strategy', 'progress', 'text'].includes(cardType) && (
            <div className={`${subBlock} text-body text-ink leading-relaxed`}>
              <FormattedText content={card.content || card.text || card.message} />
            </div>
          )}

          {card.reasoning && (
            <div className={subBlock}>
              <p className="text-label text-ink-muted mb-2">Why this matters</p>
              <p className="text-body text-ink leading-relaxed">{card.reasoning}</p>
            </div>
          )}

          {card.options?.length > 0 && (
            <div className={subBlock}>
              <p className="text-label text-ink-muted mb-3">Your options</p>
              <ul className="space-y-2">
                {card.options.map((option, idx) => (
                  <li key={idx} className={listItem}>
                    <span className="text-ink-faint shrink-0">·</span>
                    <span>{option}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {card.final_verdict && (
            <div className={subBlock}>
              <p className="text-label text-ink-muted mb-2">Final verdict</p>
              <p className="text-body text-ink">{card.final_verdict}</p>
            </div>
          )}

          {card.follow_up_questions?.length > 0 && (
            <div className={subBlock}>
              <p className="text-label text-ink-muted mb-3">Suggested questions</p>
              <div className="space-y-2">
                {card.follow_up_questions.map((question, idx) => (
                  <p key={idx} className="text-body text-ink">{question}</p>
                ))}
              </div>
            </div>
          )}

          {cardType === 'text' && card.content && (
            <div className={`${subBlock} text-body text-ink leading-relaxed`}>
              <FormattedText content={card.content || card.text || card.message} />
            </div>
          )}

          {cardType === 'advisor_response' && (
            <div className={subBlock}>
              <div className="text-body text-ink leading-relaxed">
                <FormattedText content={card.content} />
              </div>
              {card.research_used && card.sources?.length > 0 && (
                <div className="mt-4 hairline-list">
                  <p className="text-label text-ink-muted py-2">Research sources</p>
                  {card.sources.map((source, idx) => (
                    <a
                      key={idx}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-2 text-body text-pine hover:underline"
                    >
                      {source.title}
                      {source.source && (
                        <span className="text-label text-ink-faint ml-1">({source.source})</span>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {(cardType === 'routing' || card.type === 'routing_response') && (
            <div className={subBlock}>
              <FormattedText content={card.content} />
              {card.primary_cta && (
                <button
                  type="button"
                  onClick={() => {
                    if (card.primary_cta.action === 'navigate' && card.primary_cta.url) {
                      window.location.href = card.primary_cta.url;
                    }
                  }}
                  className="btn-primary mt-4"
                >
                  {card.primary_cta.text}
                </button>
              )}
            </div>
          )}

          {card.type === 'routing_suggestion' && (
            <div className={subBlock}>
              <FormattedText content={card.content} />
              <button
                type="button"
                onClick={() => {
                  if (card.deep_link) window.location.href = card.deep_link;
                }}
                className="btn-primary mt-4"
              >
                {card.action === 'go_to_learning'
                  ? 'Go to learning'
                  : card.action === 'go_to_community'
                    ? 'Visit community'
                    : 'Learn more'}
              </button>
            </div>
          )}

          {card.type === 'vision_creation_prompt' && (
            <div className={subBlock}>
              <h4 className="font-display text-h3 text-ink mb-2">{card.title}</h4>
              <p className="text-body text-ink-muted mb-3">{card.description}</p>
              {card.suggested_target && (
                <p className="text-body figure text-pine mb-3">
                  Suggested target: ${card.suggested_target.toLocaleString()}
                </p>
              )}
              <button
                type="button"
                onClick={() => {
                  if (card.deep_link) {
                    const params = new URLSearchParams();
                    if (card.title) params.set('title', card.title);
                    if (card.suggested_target) params.set('target', card.suggested_target);
                    window.location.href = `${card.deep_link}?${params.toString()}`;
                  }
                }}
                className="btn-primary mt-2"
              >
                Create vision goal
              </button>
            </div>
          )}

          {(showCitations || card.type === 'gov_verdict') && (
            <div className={`${subBlock} space-y-3`}>
              {card.gov_citations?.length > 0 && (
                <div className="hairline-list">
                  <p className="text-label text-ink-muted py-2">Government sources</p>
                  {card.gov_citations.map((cite, idx) => (
                    <a
                      key={idx}
                      href={cite.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-3 text-body text-pine hover:underline"
                    >
                      {cite.agency || cite.title}
                      {cite.relevance && (
                        <span className="block text-label text-ink-faint mt-0.5">{cite.relevance}</span>
                      )}
                    </a>
                  ))}
                </div>
              )}
              {card.advisor_citations?.length > 0 && (
                <div className="hairline-list">
                  <p className="text-label text-ink-muted py-2">Advisor sources</p>
                  {card.advisor_citations.map((cite, idx) => (
                    <a
                      key={idx}
                      href={cite.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block py-3 text-body text-pine hover:underline"
                    >
                      {cite.title || cite.domain}
                      {cite.summary && (
                        <span className="block text-label text-ink-faint mt-0.5 line-clamp-2">{cite.summary}</span>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {card.advice_payload?.verdict && card.type !== 'user_intent' && card.type !== 'user_message' && (
          <AdviceSaveActions
            advicePayload={card.advice_payload}
            visionId={visionId}
            sourceSurface={section || 'chat'}
            sourceRef={card.id}
            className="mt-3"
            compact
          />
        )}

        {canSave && card.type !== 'user_intent' && card.type !== 'user_message' && (
          <TalkToAdvisorButton
            userQuestion={card.content || card.quick_answer || card.title}
            surface={section || 'chat'}
            framing={card.content || card.quick_answer}
            advicePayload={card.advice_payload}
            escalationPacketId={card.escalation_packet_id}
            primaryAdvisorId={card.primary_advisor_id}
            compact
          />
        )}
      </motion.div>
    </>
  );
};

export default ConversationCard;
